import { useMemo } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import {
  ArrowLeft,
  Loader2,
  Square,
  FileText,
  AlertTriangle,
  Zap,
  Layers,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useRun, usePages, useCancelRun, useIngestionWebSocket } from '@/hooks/useIngestion'
import { useToast } from '@/hooks/use-toast'
import { PageDetailDrawer } from './PageDetailDrawer'
import type { PipelineRunStatus, PageItemStage, PageItem } from '@/types/ingestion'

const statusConfig: Record<PipelineRunStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }> = {
  pending: { label: 'Pending', variant: 'outline' },
  crawling: { label: 'Crawling', variant: 'default', className: 'bg-blue-600' },
  processing: { label: 'Processing', variant: 'default', className: 'bg-blue-600 animate-pulse' },
  completed: { label: 'Completed', variant: 'default', className: 'bg-green-600' },
  failed: { label: 'Failed', variant: 'destructive' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
}

function StatusBadge({ status }: { status: PipelineRunStatus }) {
  const config = statusConfig[status] ?? { label: status, variant: 'outline' as const }
  return <Badge variant={config.variant} className={config.className}>{config.label}</Badge>
}

const stageConfig: Record<PageItemStage, { label: string; className: string; pulse: boolean }> = {
  queued: { label: 'Queued', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', pulse: false },
  extracting: { label: 'Extracting', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', pulse: true },
  enriching: { label: 'Enriching', className: 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300', pulse: true },
  done: { label: 'Done', className: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300', pulse: false },
  error: { label: 'Error', className: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300', pulse: false },
}

function StagePill({ stage }: { stage: PageItemStage }) {
  const config = stageConfig[stage] ?? { label: stage, className: '', pulse: false }
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}>
      {config.pulse && (
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-current" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-current" />
        </span>
      )}
      {config.label}
    </span>
  )
}

function formatDuration(extractSec: number | null, enrichSec: number | null): string {
  const total = (extractSec ?? 0) + (enrichSec ?? 0)
  if (total === 0 && extractSec === null && enrichSec === null) return '\u2014'
  return `${total.toFixed(1)}s`
}

function getPathname(url: string): string {
  try {
    return new URL(url).pathname
  } catch {
    return url
  }
}

export function RunDetailPage() {
  const { runId = '' } = useParams<{ runId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const pageIdParam = searchParams.get('page')
  const selectedPageId = pageIdParam ? Number(pageIdParam) : null

  const { data: run, isLoading: runLoading, error: runError } = useRun(runId, {
    refetchInterval: 3000,
  })
  const isActive = run?.status === 'pending' || run?.status === 'crawling' || run?.status === 'processing'
  const { data: pagesData, isLoading: pagesLoading } = usePages(runId, {
    refetchInterval: isActive ? 3000 : false,
  })
  const { pageUpdates } = useIngestionWebSocket(runId)
  const cancelRunMut = useCancelRun()
  const { toast } = useToast()

  // Merge REST pages with live WS updates
  const mergedPages = useMemo(() => {
    const base = pagesData?.results ?? []
    return base.map((page: PageItem) => {
      const update = pageUpdates.get(page.id)
      if (update) return { ...page, ...update }
      return page
    })
  }, [pagesData, pageUpdates])

  const inFlightCount = mergedPages.filter(
    (p: PageItem) => p.stage === 'extracting' || p.stage === 'enriching'
  ).length

  const totalTriplets = mergedPages
    .filter((p: PageItem) => p.stage === 'done')
    .reduce((sum: number, p: PageItem) => sum + (p.triplet_count ?? 0), 0)

  const handleCancel = () => {
    cancelRunMut.mutate(runId, {
      onSuccess: () => toast({ title: 'Pipeline run cancelled' }),
      onError: () => toast({ title: 'Failed to cancel run', variant: 'destructive' }),
    })
  }

  const handlePageClick = (pageId: number) => {
    setSearchParams({ page: String(pageId) })
  }

  const handleDrawerClose = () => {
    setSearchParams({})
  }

  // Loading state
  if (runLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Error state
  if (runError || !run) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-destructive">Error loading pipeline run</h2>
          <p className="text-sm text-muted-foreground mt-2">
            {runError instanceof Error ? runError.message : 'Run not found'}
          </p>
          <Button variant="outline" className="mt-4" asChild>
            <Link to="/app/ingestion">Back to pipelines</Link>
          </Button>
        </div>
      </div>
    )
  }

  const canCancel = run.status === 'crawling' || run.status === 'processing'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <Link to="/app/ingestion">
            <Button variant="ghost" size="icon" className="mt-0.5">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{run.name}</h1>
              <StatusBadge status={run.status} />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {run.root_url}
              {run.started_at && (
                <> · Started {formatDistanceToNow(new Date(run.started_at), { addSuffix: true })}</>
              )}
            </p>
          </div>
        </div>
        {canCancel && (
          <Button
            variant="destructive"
            size="sm"
            onClick={handleCancel}
            disabled={cancelRunMut.isPending}
          >
            {cancelRunMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Square className="h-4 w-4 mr-2" />
            )}
            Stop
          </Button>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-2xl font-bold">{run.completed_pages}</div>
                <p className="text-xs text-muted-foreground">Pages done</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Loader2 className={`h-4 w-4 text-muted-foreground ${inFlightCount > 0 ? 'animate-spin' : ''}`} />
              <div>
                <div className="text-2xl font-bold">{inFlightCount}</div>
                <p className="text-xs text-muted-foreground">In flight</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-2xl font-bold">{totalTriplets}</div>
                <p className="text-xs text-muted-foreground">Triplets</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-2xl font-bold">{run.total_pages}</div>
                <p className="text-xs text-muted-foreground">Total pages</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress bar */}
      <Progress value={run.progress_percentage} className="h-1.5" />

      {/* Pages table */}
      {pagesLoading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : mergedPages.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
            <AlertTriangle className="h-10 w-10 text-muted-foreground" />
            <p className="text-muted-foreground">No pages discovered yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Page</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead className="text-right">Triplets</TableHead>
                <TableHead className="text-right">Chunks</TableHead>
                <TableHead className="text-right">Entities</TableHead>
                <TableHead className="text-right">Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mergedPages.map((page: PageItem) => (
                <TableRow
                  key={page.id}
                  className="cursor-pointer"
                  onClick={() => handlePageClick(page.id)}
                >
                  <TableCell>
                    <span className="font-medium text-sm truncate block max-w-[260px]">
                      {getPathname(page.url)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StagePill stage={page.stage} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{page.triplet_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{page.chunk_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{page.entity_count}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatDuration(page.extract_duration_seconds, page.enrich_duration_seconds)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Page detail drawer */}
      <PageDetailDrawer
        runId={runId}
        pageId={selectedPageId}
        onClose={handleDrawerClose}
      />
    </div>
  )
}
